// layers/heatmap.ts
import type { LayerSpecification } from "maplibre-gl";
import { SOURCES } from "../sources";
import { LAYERS } from "./constants";

// Native MapLibre heatmap. Points come from HeatmapManager (SOURCES.HEATMAP),
// each carrying a `weight` (0..1) property.
export const heatmap: LayerSpecification = {
  id: LAYERS.HEATMAP,
  type: "heatmap",
  source: SOURCES.HEATMAP,
  paint: {
    // Per-point contribution driven by the feature `weight` (0..1).
    "heatmap-weight": [
      "interpolate",
      ["linear"],
      ["get", "weight"],
      0,
      0,
      1,
      1,
    ],
    // Ramp up intensity as you zoom in so clusters stay legible street-level.
    // Pulled back from the previous 1/2/3 stops — with the old values,
    // overlapping weighted grid cells (multiple points stacking within the
    // blur radius) pushed heatmap-density up near 1.0 across most of the
    // visible field, which is why the ramp only ever showed its top
    // red/orange band. Lower intensity here means density spreads out more
    // realistically across the full 0..1 range before saturating.
    "heatmap-intensity": [
      "interpolate",
      ["linear"],
      ["zoom"],
      2,
      1,
      8,
      1.2,
      13,
      1.5,
      16,
      1.8,
    ],
    // Dark blue → yellow → white. No red/orange anywhere in this ramp.
    // White is held back until the very top of the range (0.97-1) so it
    // reads as a true "peak" marker rather than a color the field eases
    // into — most of the visible area should sit in the blue-through-yellow
    // range, with white appearing only at the single hottest point.
    // Transparent low end retained so sparse/low-weight cells don't render
    // as messy blobs.
    "heatmap-color": [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      0,
      "rgba(5, 10, 40, 0)",
      0.1,
      "rgba(8, 20, 70, 0.4)",
      0.2,
      "rgb(12, 35, 110)",
      0.3,
      "rgb(15, 55, 150)",
      0.45,
      "rgb(20, 90, 170)",
      0.58,
      "rgb(60, 130, 160)",
      0.62,
      "rgb(150, 170, 100)",
      0.7,
      "rgb(220, 205, 60)",
      0.75,
      "rgb(250, 230, 90)",
      0.8,
      "rgb(252, 245, 180)",
      0.85,
      "rgb(255, 255, 255)",
    ],
    // Grow the blur radius with zoom so it reads well from city-wide to
    // street. Increased from the previous 14/28/42/60 stops for a
    // noticeably bigger, bolder rendered blob at every zoom level — paired
    // with the larger real-world grid footprint in the simulator (bigger
    // grid spacing/extent), this makes the heatmap read as substantially
    // larger both geographically and visually.
    "heatmap-radius": [
      "interpolate",
      ["linear"],
      ["zoom"],
      8,
      24,
      11,
      48,
      14,
      72,
      16,
      100,
    ],
    // Constant opacity at all zoom levels — no fade-out. The previous
    // version faded to 0 by zoom 15 (interpolating 0.85 → 0.5 → 0), which
    // made the heatmap disappear entirely on zoom-in. Kept at a fixed 0.85
    // so the probability field stays visible for close-in inspection of a
    // target's cloud, at any zoom.
    "heatmap-opacity": 0.85,
  },
};
